import {
  cloneElement,
  forwardRef,
  isValidElement,
  useMemo,
  type ComponentRef,
  type ReactElement,
  type Ref,
} from "react";

import { Icon } from "../../atoms/icons";
import { cn } from "../../lib";
import { minTouchTargetClassName } from "../../lib/touch-target";
import { composeRefs, Pressable } from "../../primitives";
import { iconSize, touchTarget } from "../../tokens";
import { useDialogContext } from "./lib";
import type { DialogCloseProps } from "./types";

/**
 * Dialog.Close
 *
 * Closes the dialog when activated. The close handler comes from
 * `DialogContext` and is wired directly — we deliberately do NOT delegate to
 * Radix/RNR `Close`. Those compose a DOM-event-shaped `onClick` (it reads
 * `event.defaultPrevented`), but the Luce `Button`/`Pressable` `onClick` is
 * event-less for cross-platform parity. The mismatch throws before the close
 * runs, so the dialog never closes. An explicit event-less handler works
 * identically on web and native.
 *
 * - `<Dialog.Close />` — the corner close Figma draws in every modal header: a
 *   grey 24px `X` (`neutral/foreground-attenuated`) with no border or
 *   background (the designer, 2026-10-29). It isn't an IconButton look.
 * - `<Dialog.Close asChild>{button}</Dialog.Close>` — injects the close
 *   handler onto the child; the child's own `onClick` still runs first.
 * - `<Dialog.Close>label</Dialog.Close>` — wraps the content in a Pressable.
 *
 * `disabled` stops the close in every mode. The ref reaches the Pressable, or
 * the `asChild` child alongside its own ref.
 */
// Figma's modal header × (`333:29696`): icon-lg, the same on every width
const CLOSE_ICON_PX = iconSize.lg;
const CLOSE_HIT_SLOP = (touchTarget - CLOSE_ICON_PX) / 2;

type CloseChild = ReactElement<{
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  ref?: Ref<unknown>;
}>;

export const DialogClose = forwardRef<ComponentRef<typeof Pressable>, DialogCloseProps>(
  ({ asChild, children, className, testID, accessibilityLabel, disabled = false }, ref) => {
    const { requestClose } = useDialogContext();
    const childRef =
      asChild && isValidElement(children) ? (children as CloseChild).props.ref : undefined;
    const asChildRef = useMemo(() => composeRefs(ref, childRef), [ref, childRef]);

    if (asChild) {
      if (!isValidElement(children)) {
        console.warn("Dialog.Close with `asChild` requires a single React element child.");
        return null;
      }

      const child = children as CloseChild;

      return cloneElement(child, {
        ref: asChildRef,
        className: cn(child.props.className, className),
        disabled: disabled || child.props.disabled,
        onClick: () => {
          if (disabled) return;
          child.props.onClick?.();
          requestClose();
        },
      });
    }

    // The content names it, unless `accessibilityLabel` is set
    if (children !== undefined) {
      return (
        <Pressable
          ref={ref}
          onClick={requestClose}
          disabled={disabled}
          accessibilityLabel={accessibilityLabel}
          className={className}
          testID={testID}
        >
          {children}
        </Pressable>
      );
    }

    return (
      <Pressable
        ref={ref}
        onClick={requestClose}
        disabled={disabled}
        accessibilityLabel={accessibilityLabel ?? "Close"}
        // 44px to tap without growing the 24px icon: native reaches 10 past it,
        // web touch screens an invisible area (`minTouchTargetClassName`)
        hitSlop={CLOSE_HIT_SLOP}
        className={cn(
          // Its own 24px box: a header that stretches its children must not widen it
          "rounded-radius-full h-icon-lg w-icon-lg relative shrink-0 items-center justify-center",
          minTouchTargetClassName,
          // Keyboard focus on web: the ring every control draws
          "web:outline-none web:focus-visible:ring-focus web:focus-visible:ring-luce-brand-primaryBorder web:focus-visible:ring-offset-focus",
          className,
        )}
        testID={testID}
      >
        <Icon
          name="X"
          size={CLOSE_ICON_PX}
          color="neutral-foreground-attenuated"
          className="shrink-0"
        />
      </Pressable>
    );
  },
);

DialogClose.displayName = "Dialog.Close";
